import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_2bde1c9b-86e8-45d3-80bd-0f0476a263ab/artifacts/ur3i0a8o_BAckground.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="forgot-password-success"
      >
        <div className="absolute inset-0 bg-[#0A0A0B]/90" />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 bg-[#1A1A1D] p-10 rounded-lg metallic-border cyber-glow max-w-md w-full text-center"
        >
          <div className="inline-block p-4 bg-[#00FF88]/10 rounded-full mb-6">
            <CheckCircle className="h-16 w-16 text-[#00FF88]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Email envoyé !</h2>
          <p className="text-[#B8B8BA] mb-6">
            Si un compte existe avec l'adresse <strong className="text-white">{email}</strong>, 
            vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
          </p>
          <p className="text-[#6B6B6E] text-sm mb-6">
            Pensez à vérifier vos spams si vous ne trouvez pas l'email.
          </p>
          <Link
            to="/connexion"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour à la connexion</span>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_2bde1c9b-86e8-45d3-80bd-0f0476a263ab/artifacts/ur3i0a8o_BAckground.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="forgot-password-page"
    >
      <div className="absolute inset-0 bg-[#0A0A0B]/90" />
      
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-[#1A1A1D] p-10 rounded-lg metallic-border cyber-glow">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Mot de passe oublié ?</h1>
            <p className="text-[#B8B8BA]">
              Entrez votre email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
            </p>
          </div>

          {error && (
            <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C]/30 text-[#FF3B5C] p-4 rounded-lg mb-6 flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[#B8B8BA] text-sm font-medium mb-2">
                Adresse email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6B6B6E]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#222226] text-white pl-12 pr-4 py-3 rounded-lg border border-[#3A3A3D] focus:border-[#00FFFF] focus:outline-none transition-colors"
                  placeholder="votre@email.com"
                  required
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 flex items-center justify-center"
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent" />
              ) : (
                'Envoyer le lien de réinitialisation'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link
              to="/connexion"
              className="text-[#00FFFF] hover:underline inline-flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Retour à la connexion</span>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
